import React, { useEffect, useState } from 'react';
import { isAbortError } from '../services/apiClient';
import { Icon, STATUS_MAP, StatusChip } from '../components/sp-primitives';
import { Modal } from '../components/sp-overlays';
import { PShell, useApp } from '../components/sp-shell';

export function PCompanySyncLogs() {
  const app = useApp();
  const companyId = Number(app.params?.companyId);
  const company = app.companies.find((c) => c.id === companyId);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [payload, setPayload] = useState(null);
  const [syncingFields, setSyncingFields] = useState('');
  const [prefModal, setPrefModal] = useState(false);
  const [prefSyncing, setPrefSyncing] = useState(false);
  const [prefBatchSize, setPrefBatchSize] = useState(10);
  const [prefTotal, setPrefTotal] = useState(null);
  const [clearModal, setClearModal] = useState(false);
  const [clearPassword, setClearPassword] = useState('');
  const [clearing, setClearing] = useState(false);
  const isOwnerUser = Number(app.currentUser?.id || 0) === 1;

  const fmtOccurred = (d) => {
    if (!d) return '—';
    const s = String(d).trim();
    const x = s.includes('T') || s.includes('Z') || s.includes('+')
      ? new Date(s)
      : new Date(s.replace(' ', 'T') + 'Z');
    if (Number.isNaN(x.getTime())) return '—';
    return x.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const statusChip = (s) => {
    if (s === 'success') return <StatusChip kind="green">Sucesso</StatusChip>;
    if (s === 'error') return <StatusChip kind="red">Erro</StatusChip>;
    if (s === 'processing') return <StatusChip kind={STATUS_MAP.processing.kind}>Processando</StatusChip>;
    if (s === 'running') return <StatusChip kind="neutral">Em execução</StatusChip>;
    if (s === 'pending') return <StatusChip kind="amber">Aguardando CRON</StatusChip>;
    return <StatusChip kind="neutral">{s || '—'}</StatusChip>;
  };

  const metaInt = (m, key) => {
    if (!m || typeof m !== 'object') return null;
    const v = m[key];
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string' && /^\d+$/.test(v)) return parseInt(v, 10);
    return null;
  };

  const estimatedHttpFromMeta = (m) => {
    const e = metaInt(m, 'estimated_http_requests');
    if (e !== null) return e;
    const segStart = metaInt(m, 'segment_start_offset');
    const segEnd = metaInt(m, 'segment_end_offset');
    const pageSize = metaInt(m, 'page_size') || 100;
    if (segStart !== null && segEnd !== null && segEnd > segStart) {
      return Math.max(1, Math.ceil((segEnd - segStart) / Math.max(1, pageSize)));
    }
    const rt = metaInt(m, 'reported_api_total');
    if (rt === null) return null;
    if (rt <= 0) return 1;
    return Math.max(1, Math.ceil(rt / Math.max(1, pageSize)));
  };

  const reqHttpLabel = (r) => {
    const m = r?.metadata;
    const cur = metaInt(m, 'api_request_count');
    const tot = estimatedHttpFromMeta(m);
    if (cur === null && tot === null) return '—';
    if (tot === null) return cur !== null ? `${cur}/…` : '—';
    if (cur === null) return `—/${tot}`;
    return `${cur}/${tot}`;
  };

  useEffect(() => {
    if (!companyId || !app.authToken) {
      setPayload(null);
      return undefined;
    }
    const ac = new AbortController();
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const data = await app.fetchCompanySyncLogs(
          companyId,
          { page: String(page), per_page: '20' },
          { signal: ac.signal },
        );
        setPayload(data);
      } catch (e) {
        if (isAbortError(e)) return;
        setErr(e.message || 'Falha ao carregar logs');
        setPayload(null);
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => {
      ac.abort();
    };
  }, [companyId, page, app.authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId || !app.authToken || !payload?.data) return undefined;
    const hasActive = payload.data.some((r) => r.status === 'pending' || r.status === 'processing' || r.status === 'running');
    if (!hasActive) return undefined;
    let requestAc = null;
    const id = setInterval(async () => {
      requestAc?.abort();
      requestAc = new AbortController();
      try {
        const data = await app.fetchCompanySyncLogs(
          companyId,
          { page: String(page), per_page: '20' },
          { signal: requestAc.signal },
        );
        setPayload(data);
      } catch (e) {
        if (isAbortError(e)) return;
        // mantém último payload
      }
    }, 2500);
    return () => {
      clearInterval(id);
      requestAc?.abort();
    };
  }, [companyId, page, app.authToken, payload]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = payload?.data || [];
  const lastPage = payload?.last_page || 1;
  const livePolling = rows.some((r) => r.status === 'pending' || r.status === 'processing' || r.status === 'running');

  const triggerSyncCompanies = async () => {
    if (!companyId || syncingFields) return;
    setSyncingFields('companies');
    try {
      const data = await app.syncCompanyFieldsCompanies(companyId);
      app.toast('success', 'Sincronização de Campos', data?.message || 'Sincronização de Companias enfileirada.');
      setPage(1);
      const logs = await app.fetchCompanySyncLogs(companyId, { page: '1', per_page: '20' });
      setPayload(logs);
    } catch (e) {
      app.toast('error', 'Sincronização de Campos', e.message || 'Falha ao enfileirar Companias.');
    } finally {
      setSyncingFields('');
    }
  };

  const triggerSyncCostCenters = async () => {
    if (!companyId || syncingFields) return;
    setSyncingFields('cost-centers');
    try {
      const data = await app.syncCompanyFieldsCostCenters(companyId);
      app.toast('success', 'Sincronização de Campos', data?.message || 'Sincronização de Centros de Custo enfileirada.');
      setPage(1);
      const logs = await app.fetchCompanySyncLogs(companyId, { page: '1', per_page: '20' });
      setPayload(logs);
    } catch (e) {
      app.toast('error', 'Sincronização de Campos', e.message || 'Falha ao enfileirar Centros de Custo.');
    } finally {
      setSyncingFields('');
    }
  };

  if (!companyId) {
    return (
      <PShell active="companies">
        <div className="sp-screen-head">
          <h1 className="sp-screen-head__title">Empresa inválida</h1>
        </div>
        <button type="button" className="sp-btn sp-btn-outline" onClick={() => app.navigate('companies')}>
          Voltar às empresas
        </button>
      </PShell>
    );
  }

  return (
    <PShell active="companies">
      <button type="button" className="sp-btn sp-btn-ghost sp-form-back" onClick={() => app.navigate('companies')}>
        ← Voltar para empresas
      </button>
      <div className="sp-screen-head">
        <div>
          <div className="sp-caps sp-screen-head__caps">Tarefas de sincronização</div>
          <h1 className="sp-screen-head__title">Logs — {company?.name || `Empresa #${companyId}`}</h1>
          <div className="sp-screen-head__meta sp-mono">
            company_id:{companyId} · Ordenação: mais recente primeiro (tarefa «Sincronização de cadastros» tipo Credores grava credores na base local; pagamentos por lote).
          </div>
        </div>
      </div>
      <div className="sp-settings-actions" style={{ marginBottom: 12 }}>
        <button type="button" className="sp-btn sp-btn-outline" onClick={triggerSyncCompanies} disabled={Boolean(syncingFields)}>
          <Icon name="history" size={14} /> {syncingFields === 'companies' ? 'A enfileirar...' : 'Sincronizar Companhias'}
        </button>
        <button type="button" className="sp-btn sp-btn-outline" onClick={triggerSyncCostCenters} disabled={Boolean(syncingFields)}>
          <Icon name="history" size={14} /> {syncingFields === 'cost-centers' ? 'A enfileirar...' : 'Sincronizar Centros de Custo'}
        </button>
        <button
          type="button"
          className="sp-btn sp-btn-outline"
          disabled={prefSyncing}
          onClick={async () => {
            try {
              const [prefData, kpiData] = await Promise.all([
                app.fetchCreditorPrefSync(),
                app.fetchCompanyCreditorsKpis(companyId),
              ]);
              const batchSize = Number(prefData?.effective?.batch_size ?? 10);
              const filter    = prefData?.effective?.filter ?? 'employees';
              const total     = filter === 'employees'
                ? Number(kpiData?.active_employees ?? 0)
                : Number(kpiData?.active_creditors ?? 0);
              setPrefBatchSize(batchSize);
              setPrefTotal(total);
            } catch {
              setPrefBatchSize(10);
              setPrefTotal(null);
            }
            setPrefModal(true);
          }}
        >
          <Icon name="history" size={14} /> Sincronizar Preferências
        </button>
        {isOwnerUser && (
          <button type="button" className="sp-btn sp-btn-outline" style={{ color: 'var(--ft-red)', borderColor: 'var(--ft-red)' }}
            onClick={() => { setClearPassword(''); setClearModal(true); }}>
            <Icon name="trash" size={13} /> Limpar Fila
          </button>
        )}
      </div>

      {livePolling && !loading && !err && (
        <div className="sp-sync-live-chip-wrap">
          <span className="sp-sync-live-chip">Sincronização em Andamento - Acompanhe Abaixo</span>
        </div>
      )}

      <div className="sp-card sp-card-flush sp-card-creditors">
        {err && <div className="sp-creditors-banner sp-creditors-banner--err">{err}</div>}
        {loading && !err && <div className="sp-creditors-banner">Carregando…</div>}
        <table className="sp-table">
          <thead>
            <tr>
              <th>Tarefa</th>
              <th>Tipo</th>
              <th>Afetados</th>
              <th title="Requisições GET já feitas / total estimado (ceil(count ÷ 100))">Req. HTTP</th>
              <th>Detalhes</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {!loading &&
              rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.task}</td>
                  <td>{r.type}</td>
                  <td className="sp-mono">{r.affected}</td>
                  <td className="sp-mono">{reqHttpLabel(r)}</td>
                  <td>
                    <div className="sp-mono sp-sync-log-dt">{fmtOccurred(r.occurred_at)}</div>
                    {r.details ? <div className="sp-sync-log-details">{r.details}</div> : null}
                  </td>
                  <td>{statusChip(r.status)}</td>
                </tr>
              ))}
            {!loading && !err && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="sp-td-empty">
                  Nenhum registo de sincronização para esta empresa.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {lastPage > 1 && (
          <div className="sp-creditors-pager">
            <button type="button" className="sp-btn sp-btn-outline sp-btn-tiny" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Anterior
            </button>
            <span className="sp-mono">
              Página {page} de {lastPage}
            </span>
            <button type="button" className="sp-btn sp-btn-outline sp-btn-tiny" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
              Próxima
            </button>
          </div>
        )}
      </div>
      <Modal open={prefModal} onClose={() => !prefSyncing && setPrefModal(false)} width={500}>
        <div className="sp-modal__head">
          <div><div className="sp-modal__head-title">Sincronizar Preferências</div></div>
          <button type="button" className="sp-btn sp-btn-ghost sp-modal__close" onClick={() => !prefSyncing && setPrefModal(false)} disabled={prefSyncing}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="sp-modal__body sp-modal__body--stack-lg">
          <div className="sp-sidebar-card__muted">
            Este botão aciona a sincronia dos seguintes campos preferências de <strong>CompanhiaID</strong> e <strong>CentroCustoID</strong> de todos os credores. Ele estabelece que os valores do último pagamento serão preferências para os próximos pagamentos criados nesta plataforma.
          </div>
          {prefTotal > 0 && prefBatchSize > 0 && (() => {
            const mins = Math.ceil(prefTotal / prefBatchSize);
            return <div className="sp-delete-banner">{`Esta ação vai levar aproximadamente ${mins} minuto${mins !== 1 ? 's' : ''} (${prefTotal} credores ÷ ${prefBatchSize} por tarefa).`}</div>;
          })()}
          <div className="sp-delete-banner">Deseja continuar?</div>
          <div className="sp-modal-actions">
            <button type="button" className="sp-btn sp-btn-outline" onClick={() => setPrefModal(false)} disabled={prefSyncing}>Não</button>
            <button type="button" className="sp-btn sp-btn-accent" disabled={prefSyncing} onClick={async () => {
              setPrefSyncing(true);
              try {
                const res = await app.syncCreditorPreferences(companyId);
                setPrefModal(false);
                app.toast('success', 'Preferências', res?.message || 'Sincronização enfileirada.');
                setPage(1);
                const logs = await app.fetchCompanySyncLogs(companyId, { page: '1', per_page: '20' });
                setPayload(logs);
              } catch (e) {
                app.toast('error', 'Falha', e?.message || 'Não foi possível enfileirar.');
              } finally { setPrefSyncing(false); }
            }}>
              {prefSyncing ? 'Enfileirando…' : 'Sim'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={clearModal} onClose={() => !clearing && setClearModal(false)} width={460}>
        <div className="sp-modal__head">
          <div>
            <div className="sp-modal__head-title">Limpar Fila de Sincronização</div>
            <div className="sp-modal__head-desc">Esta ação não pode ser desfeita.</div>
          </div>
          <button type="button" className="sp-btn sp-btn-ghost sp-modal__close" onClick={() => !clearing && setClearModal(false)} disabled={clearing}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="sp-modal__body sp-modal__body--stack-lg">
          <div className="sp-sidebar-card__muted">
            Todos os registos de <strong>company_creditor_sync_requests</strong> e <strong>company_sync_logs</strong> serão apagados permanentemente.
          </div>
          <div className="sp-delete-banner">
            Confirme sua senha para continuar.
          </div>
          <div>
            <label className="sp-caps sp-label">Senha</label>
            <input
              className="sp-input"
              type="password"
              value={clearPassword}
              onChange={(e) => setClearPassword(e.target.value)}
              disabled={clearing}
              placeholder="Digite sua senha"
            />
          </div>
          <div className="sp-modal-actions">
            <button type="button" className="sp-btn sp-btn-outline" onClick={() => setClearModal(false)} disabled={clearing}>
              Cancelar
            </button>
            <button
              type="button"
              className="sp-btn sp-btn-danger-solid"
              disabled={clearing || !clearPassword.trim()}
              onClick={async () => {
                setClearing(true);
                try {
                  await app.clearSyncQueue(clearPassword);
                  setClearModal(false);
                  setPayload(null);
                  app.toast('success', 'Fila limpa', 'Todos os logs e tarefas foram removidos.');
                } catch (e) {
                  app.toast('error', 'Falha', e?.message || 'Não foi possível limpar a fila.');
                } finally {
                  setClearing(false);
                }
              }}
            >
              {clearing ? 'A limpar…' : 'Confirmar limpeza'}
            </button>
          </div>
        </div>
      </Modal>
    </PShell>
  );
}
